import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Tabs from "../../components/Tabs";
import Alert from "../../components/Alert";
import StudentPicker from "../../components/StudentPicker";
import { createLookupApi } from "../../api/lookupApi";
import { saveTestimonial, listTestimonials } from "../../api/testimonialApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");

const emptyForm = {
  examName: "",
  examBoard: "",
  examSession: "",
  boardRoll: "",
  boardRegNo: "",
  examGrade: "",
  examGpa: "",
  passingYear: "",
};

/**
 * Direct port of digicampusbd's real, heavily-used {@code testimonial-add}/
 * {@code testimonial-download} pages (2,481 real testimonials). Consolidates
 * the add/edit form and the report-list-with-print-view into one page, same
 * pattern as every other consolidated page in this migration. The Jasper
 * download itself is not ported (established precedent) — "View" renders
 * the same data inline instead.
 */
export default function TestimonialPage() {
  const [tab, setTab] = useState("add");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Testimonial</h1>
      </div>
      <Tabs
        tabs={[
          { key: "add", label: "Add / Edit" },
          { key: "list", label: "List & View" },
        ]}
        active={tab}
        onChange={setTab}
      />
      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {tab === "add" && <AddTab setError={setError} setSuccess={setSuccess} />}
      {tab === "list" && <ListTab setError={setError} />}
    </MainLayout>
  );
}

function AddTab({ setError, setSuccess }) {
  const [student, setStudent] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!student) {
      setError("Select a student first.");
      return;
    }
    setSaving(true);
    try {
      const res = await saveTestimonial({
        ...form,
        examGpa: Number(form.examGpa),
        identificationId: student.identificationId,
      });
      setSuccess(res.message);
      setForm(emptyForm);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save testimonial.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <StudentPicker onSelect={setStudent} />
      <form onSubmit={handleSubmit} className="user-form">
        <label>Exam Name</label>
        <input value={form.examName} onChange={setField("examName")} required />
        <label>Exam Board</label>
        <input value={form.examBoard} onChange={setField("examBoard")} required />
        <label>Exam Session</label>
        <input value={form.examSession} onChange={setField("examSession")} required />
        <label>Board Roll</label>
        <input value={form.boardRoll} onChange={setField("boardRoll")} required />
        <label>Board Registration No.</label>
        <input value={form.boardRegNo} onChange={setField("boardRegNo")} required />
        <label>Exam Grade</label>
        <input value={form.examGrade} onChange={setField("examGrade")} required />
        <label>Exam GPA</label>
        <input type="number" step="0.01" value={form.examGpa} onChange={setField("examGpa")} required />
        <label>Passing Year</label>
        <input value={form.passingYear} onChange={setField("passingYear")} required />
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving || !student}>
            {saving ? "Saving..." : "Save Testimonial"}
          </button>
        </div>
      </form>
    </>
  );
}

function ListTab({ setError }) {
  const [academicYears, setAcademicYears] = useState([]);
  const [academicYear, setAcademicYear] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) setAcademicYear(years[0].name);
    });
  }, []);

  useEffect(() => {
    if (!academicYear) return;
    setLoading(true);
    listTestimonials(academicYear)
      .then(setRows)
      .catch(() => setError("Failed to load testimonials."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear]);

  return (
    <>
      <div className="attendance-filters">
        <label>
          Academic Year
          <select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={[
            { key: "testimonialSerial", label: "Serial" },
            { key: "studentName", label: "Student" },
            { key: "className", label: "Class" },
            { key: "examName", label: "Exam" },
            { key: "examBoard", label: "Board" },
            { key: "examGpa", label: "GPA" },
            { key: "issueDate", label: "Issue Date" },
          ]}
          rows={rows}
          actions={(row) => (
            <button type="button" className="btn-link" onClick={() => setViewing(row)}>
              View
            </button>
          )}
        />
      )}

      {viewing && (
        <div className="public-card" style={{ marginTop: 16, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, padding: "1.5rem" }}>
          <div className="page-header">
            <h3>Testimonial — {viewing.testimonialSerial}</h3>
            <button type="button" className="btn-link" onClick={() => setViewing(null)}>
              Close
            </button>
          </div>
          <div className="table-scroll">
          <table className="attendance-table">
            <tbody>
              <tr>
                <th>Student Name</th>
                <td>{viewing.studentName}</td>
              </tr>
              <tr>
                <th>Father Name</th>
                <td>{viewing.fatherName}</td>
              </tr>
              <tr>
                <th>Mother Name</th>
                <td>{viewing.motherName}</td>
              </tr>
              <tr>
                <th>Class / Group</th>
                <td>
                  {viewing.className} / {viewing.groupName}
                </td>
              </tr>
              <tr>
                <th>Section / Shift</th>
                <td>
                  {viewing.sectionName} / {viewing.shiftName}
                </td>
              </tr>
              <tr>
                <th>Address</th>
                <td>
                  {viewing.postOffice}, {viewing.thana}, {viewing.zilla}
                </td>
              </tr>
              <tr>
                <th>Exam</th>
                <td>
                  {viewing.examName} ({viewing.examSession}), {viewing.examBoard}
                </td>
              </tr>
              <tr>
                <th>Board Roll / Reg No.</th>
                <td>
                  {viewing.boardRoll} / {viewing.boardRegNo}
                </td>
              </tr>
              <tr>
                <th>Grade / GPA</th>
                <td>
                  {viewing.examGrade} / {viewing.examGpa}
                </td>
              </tr>
              <tr>
                <th>Passing Year</th>
                <td>{viewing.passingYear}</td>
              </tr>
              <tr>
                <th>Issue Date</th>
                <td>{viewing.issueDate}</td>
              </tr>
            </tbody>
          </table>
          </div>
        </div>
      )}
    </>
  );
}
